import { GET_CONTACT, GET_CONTACTS } from "@/store/action-types";

/**
 * Composable for contact data operations
 * Works with Vue Options API components
 */
export function useContactData(store: any) {
  const getContact = (
    selectedBucket: any,
    account: any,
    emit?: (event: string, ...args: any[]) => void
  ) => {
    if (!selectedBucket?.contactId || !account?.organizationId) {
      return Promise.resolve(null);
    }

    return store
      .dispatch(GET_CONTACT, {
        id: selectedBucket.contactId,
        organizationId: account.organizationId,
      })
      .then((res: any) => {
        if (res) {
          const existingPhones = selectedBucket?.contact?.phones;
          selectedBucket.contact = res;
          if (existingPhones && Array.isArray(existingPhones) && existingPhones.length > 0) {
            if (!res.phones || !Array.isArray(res.phones) || res.phones.length === 0) {
              selectedBucket.contact.phones = existingPhones;
            }
          }
          if (emit) {
            emit("refresh-buckets");
          }
        }
        return res;
      });
  };

  const getContacts = (account: any, searchTerm?: string, mobileNumber?: string) => {
    if (!account?.organizationId) {
      return Promise.resolve(null);
    }

    const params: any = {
      organizationId: account.organizationId,
    };
    if (mobileNumber) {
      params.mobileNumber = mobileNumber;
    } else {
      params.searchTerm = searchTerm || "";
    }

    return store.dispatch(GET_CONTACTS, params).then((res: any) => {
      if (res) {
        // Contact list updated
      }
      return res;
    });
  };

  const computeContactPhoneNumbers = (selectedBucket: any) => {
    const numbers: Array<{ id?: string; type: string; number: string }> = [];
    const contact = selectedBucket?.contact;

    if (!contact) return numbers;

    if (contact.phones && Array.isArray(contact.phones) && contact.phones.length > 0) {
      contact.phones.forEach((p: any) => {
        if (p && p.number && typeof p.number === "string" && p.number.trim().length > 0) {
          const type =
            p.type === "DIRECT"
              ? "Direct"
              : p.type === "MOBILE"
                ? "Mobile"
                : p.routeType === "DIRECT"
                  ? "Direct"
                  : p.routeType === "MOBILE"
                    ? "Mobile"
                    : p.routeType === "IVR"
                      ? "IVR"
                      : p.routeType === "GATEKEEPER"
                        ? "Gate Keeper"
                        : p.type === "WORK"
                          ? "Work"
                          : p.type === "HOME"
                            ? "Home"
                            : "Other";
          numbers.push({
            id: p.id,
            type,
            number: p.number,
          });
        }
      });
    }

    if (numbers.length === 0) {
      if (
        contact.labeledAdditionalPhoneNumbers &&
        Array.isArray(contact.labeledAdditionalPhoneNumbers)
      ) {
        numbers.push(...contact.labeledAdditionalPhoneNumbers);
      }
      if (
        contact.directNumber &&
        typeof contact.directNumber === "string" &&
        contact.directNumber.trim()
      ) {
        numbers.push({
          type: "Direct",
          number: contact.directNumber,
        });
      }
      if (
        contact.mobileNumber &&
        typeof contact.mobileNumber === "string" &&
        contact.mobileNumber.trim()
      ) {
        numbers.push({
          type: "Mobile",
          number: contact.mobileNumber,
        });
      }
    }

    return numbers.filter(
      (item, index, arr) =>
        item?.number && arr.findIndex((i) => i?.number === item?.number) === index
    );
  };

  const computeContactEmails = (selectedBucket: any) => {
    const emails: Array<{
      email: string;
      type?: string;
      isPrimary?: boolean;
      verificationStatus?: string | null;
    }> = [];
    const contact = selectedBucket?.contact;
    if (!contact) return emails;

    // Primary source: contact.emails (ContactEmail records)
    if (contact.emails && Array.isArray(contact.emails) && contact.emails.length > 0) {
      contact.emails.forEach((e: any) => {
        if (e && e.email && typeof e.email === "string" && e.email.trim().length > 0) {
          emails.push({
            email: e.email,
            type: e.type,
            isPrimary: e.isPrimary,
            verificationStatus: e.verificationStatus || null,
          });
        }
      });
    }

    // Fallback: flat fields
    if (emails.length === 0) {
      if (contact.email && typeof contact.email === "string" && contact.email.trim()) {
        emails.push({
          email: contact.email,
          isPrimary: true,
          verificationStatus: contact.emailVerificationStatus || null,
        });
      }
      if (Array.isArray(contact.additionalEmails)) {
        contact.additionalEmails.forEach((addr: string) => {
          if (addr && typeof addr === "string" && addr.trim()) {
            emails.push({ email: addr });
          }
        });
      }
    }

    // Deduplicate by email (case-insensitive)
    return emails.filter(
      (item, index, arr) =>
        item?.email &&
        arr.findIndex((i) => i?.email?.toLowerCase() === item?.email?.toLowerCase()) === index
    );
  };

  return {
    getContact,
    getContacts,
    computeContactPhoneNumbers,
    computeContactEmails,
  };
}
